
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTravelAutocomplete } from '../../hooks/useTravelAutocomplete';
import { MapPin, Plane, Train } from 'lucide-react';

const TravelAutocompleteInput = ({
    value,
    onChange,
    placeholder,
    className,
    onSelect,
    maxLength
}) => {
    const { suggestions, loading, search } = useTravelAutocomplete();
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const inputRef = useRef(null);
    // Tracks whether a suggestion is being pressed so we can suppress the blur-close
    const selectingRef = useRef(false);

    // Reposition dropdown whenever it becomes visible or window resizes/scrolls
    useLayoutEffect(() => {
        if (!showSuggestions || !inputRef.current) return;

        const updatePos = () => {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 8,
                left: rect.left,
                width: Math.max(rect.width, 320),
                zIndex: 99999,
                background: '#fff',
                border: '1px solid #f3f4f6',
                borderRadius: '12px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '8px 0',
            });
        };

        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        return () => {
            window.removeEventListener('scroll', updatePos, true);
            window.removeEventListener('resize', updatePos);
        };
    }, [showSuggestions]);

    const handleChange = (e) => {
        const val = e.target.value;
        onChange(val);
        search(val);
        setShowSuggestions(true);
    };

    const handleBlur = () => {
        // Delay closing so onPointerDown on a suggestion fires handleSelect first.
        setTimeout(() => {
            if (!selectingRef.current) {
                setShowSuggestions(false);
            }
            selectingRef.current = false;
        }, 200);
    };

    const handleSelect = (item) => {
        selectingRef.current = false;
        if (onSelect) {
            onSelect(item);
        } else {
            onChange(item.label || item.name || item.description);
        }
        setShowSuggestions(false);
    };

    const getIcon = (type) => {
        let IconComp = MapPin;
        let bgClass = "bg-gray-100";
        let textClass = "text-gray-600";

        if (type === 'station') {
            IconComp = Train;
            bgClass = "bg-orange-100";
            textClass = "text-orange-600";
        } else if (type === 'airport') {
            IconComp = Plane;
            bgClass = "bg-sky-100";
            textClass = "text-sky-600";
        }

        return (
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${bgClass} flex-shrink-0`}>
                <IconComp size={18} strokeWidth={2} className={textClass} />
            </div>
        );
    };

    const suggestionItem = (item, key) => (
        <div
            key={key}
            className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-50 last:border-0 transition-all duration-200"
            onPointerDown={() => { selectingRef.current = true; handleSelect(item); }}
        >
            <div className="flex-shrink-0">
                {getIcon(item.type)}
            </div>
            <div className="flex flex-col min-w-0">
                <span className="font-semibold text-sm text-gray-900 leading-tight text-left">
                    {item.label}
                </span>
                <span className="text-xs text-gray-500 mt-1 leading-tight text-left">
                    {item.subLabel || item.city || "City"}
                </span>
            </div>
        </div>
    );

    const dropdown = showSuggestions && suggestions.length > 0 && (
        <div className="autocomplete-suggestions-container" style={dropdownStyle}>
            {(!value || value.length < 1) ? (
                <>
                    <div className="px-4 py-2 text-xs font-bold text-left text-gray-400 uppercase tracking-wider bg-gray-50/50 mb-1">
                        Most Searched Airports
                    </div>
                    {suggestions.filter(s => s.type === 'airport').map((item, i) =>
                        suggestionItem(item, `air-${i}`)
                    )}
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50/50 mb-1 mt-2 border-t border-gray-100 pt-3 text-left">
                        Most Searched Stations
                    </div>
                    {suggestions.filter(s => s.type === 'station').map((item, i) =>
                        suggestionItem(item, `stat-${i}`)
                    )}
                </>
            ) : (
                suggestions.map((item, i) => suggestionItem(item, i))
            )}
            {loading && (
                <div className="p-3 text-center text-gray-400 text-xs animate-pulse">
                    Getting best routes...
                </div>
            )}
        </div>
    );

    return (
        <div className="relative w-full">
            <input
                ref={inputRef}
                type="text"
                className={className}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                maxLength={maxLength}
                onBlur={handleBlur}
                onFocus={() => {
                    search(value);
                    setShowSuggestions(true);
                }}
            />
            {/* Portal: renders at document.body, completely outside all stacking contexts */}
            {dropdown && ReactDOM.createPortal(dropdown, document.body)}
        </div>
    );
};

export default TravelAutocompleteInput;
